import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Linking } from 'react-native';
import { Image } from '../common/OptimizedImage';
import { Ionicons } from '@expo/vector-icons';
import { Post } from '../../types/post';
import { Avatar, ImageViewer } from '../common';
import { colors } from '../../utils/colors';
import { typography } from '../../utils/typography';
import { spacing, borderRadius } from '../../utils/theme';
import { getRelativeTime } from '../../utils/date';
import { detectHashtags, detectMentions, detectUrls } from '../../utils/text';
import { formatDuration, getVideoDuration, convertPostToVideoPost } from '../../utils/video';
import { VideoDurationBadge } from '../video/VideoDurationBadge';

interface PostCardProps {
  post: Post;
  onPress?: () => void;
  onLike?: () => void;
  onRepost?: () => void;
  onReply?: () => void;
  onBookmark?: () => void;
  onAuthorPress?: () => void;
}

export const PostCard: React.FC<PostCardProps> = React.memo(({
  post,
  onPress,
  onLike,
  onRepost,
  onReply,
  onBookmark,
  onAuthorPress,
}) => {
  const isLiked = !!post.viewer?.like;
  const isReposted = !!post.viewer?.repost;
  const isBookmarked = !!post.viewer?.bookmark;
  const [isImageViewVisible, setIsImageViewVisible] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const embed = post.embed;
  const images = embed?.images || [];
  const external = embed?.external;
  
  // 動画の判定を強化(直接のビデオ、またはメディア付きレコード内のビデオ、またはYouTube)
  const isVideo = 
    embed?.$type?.includes('app.bsky.embed.video') || 
    (embed?.$type?.includes('app.bsky.embed.recordWithMedia') && 
     (embed as any).media?.$type?.includes('app.bsky.embed.video'));
  
  const isYouTube = !!(external?.uri?.match(/youtube\.com|youtu\.be/));
    
  const duration = (isVideo || isYouTube) ? formatDuration(getVideoDuration(post) || 0) : null;

  const imageUris = images.map(img => ({ uri: img.fullsize }));

  // テキストをパースしてリッチ表示用のコンポーネントに変換
  const renderRichText = (text: string) => {
    const hashtags = detectHashtags(text);
    const mentions = detectMentions(text);
    const urls = detectUrls(text);

    // テキストを分割してリッチコンテンツを作成
    const parts: { text: string; type: 'text' | 'hashtag' | 'mention' | 'url' }[] = [];
    let lastIndex = 0;

    // すべてのマッチをまとめて位置順にソート
    const allMatches: { start: number; end: number; text: string; type: 'hashtag' | 'mention' | 'url' }[] = [];

    hashtags.forEach(tag => {
      const index = text.indexOf(tag, lastIndex);
      if (index !== -1) {
        allMatches.push({ start: index, end: index + tag.length, text: tag, type: 'hashtag' });
      }
    });

    mentions.forEach(mention => {
      const index = text.indexOf(mention, lastIndex);
      if (index !== -1) {
        allMatches.push({ start: index, end: index + mention.length, text: mention, type: 'mention' });
      }
    });

    urls.forEach(url => {
      const index = text.indexOf(url, lastIndex);
      if (index !== -1) {
        allMatches.push({ start: index, end: index + url.length, text: url, type: 'url' });
      }
    });

    // 位置順にソート
    allMatches.sort((a, b) => a.start - b.start);

    // テキストを分割
    lastIndex = 0;
    allMatches.forEach(match => {
      if (match.start > lastIndex) {
        parts.push({ text: text.substring(lastIndex, match.start), type: 'text' });
      }
      parts.push({ text: match.text, type: match.type });
      lastIndex = match.end;
    });

    if (lastIndex < text.length) {
      parts.push({ text: text.substring(lastIndex), type: 'text' });
    }

    return (
      <Text style={styles.text}>
        {parts.map((part, index) => {
          switch (part.type) {
            case 'hashtag':
              return (
                <Text key={index} style={styles.hashtag}>
                  {part.text}
                </Text>
              );
            case 'mention':
              return (
                <Text key={index} style={styles.mention}>
                  {part.text}
                </Text>
              );
            case 'url':
              return (
                <Text
                  key={index}
                  style={styles.link}
                  onPress={() => Linking.openURL(part.text)}
                >
                  {part.text}
                </Text>
              );
            default:
              return <Text key={index}>{part.text}</Text>;
          }
        })}
      </Text>
    );
  };

  return (
    <View style={styles.wrapper}>
      {/* リポスト元表示 */}
      {post.reason && post.reason.$type === 'app.bsky.feed.defs#reasonRepost' && (
        <View style={styles.repostReason}>
          <Ionicons name="repeat" size={14} color={colors.repost} />
          <Text style={styles.repostReasonText}>
            {post.reason.by.displayName || post.reason.by.handle} がリポスト
          </Text>
        </View>
      )}

      {/* 返信先表示 */}
      {post.record.reply && (
        <View style={styles.replyTo}>
          <Ionicons name="arrow-undo" size={14} color={colors.textSecondary} />
          <Text style={styles.replyToText}>返信先</Text>
        </View>
      )}
      
      <TouchableOpacity
        style={styles.container}
        onPress={onPress}
        activeOpacity={0.7}
        disabled={!onPress}
      >
      <TouchableOpacity 
        style={styles.avatarContainer}
        onPress={onAuthorPress}
        activeOpacity={0.7}
      >
        <Avatar uri={post.author.avatar} size="medium" />
      </TouchableOpacity>

      <View style={styles.content}>
        {/* ヘッダー */}
        <TouchableOpacity 
          style={styles.header} 
          onPress={onAuthorPress}
          activeOpacity={0.7}
          disabled={!onAuthorPress}
        >
          <Text style={styles.displayName} numberOfLines={1}>
            {post.author.displayName || post.author.handle}
          </Text>
          <Text style={styles.handle} numberOfLines={1}>
            @{post.author.handle}
          </Text>
          <Text style={styles.separator}>·</Text>
          <Text style={styles.time}>{getRelativeTime(post.record.createdAt)}</Text>
        </TouchableOpacity>

        {/* 投稿本文 */}
        {renderRichText(post.record.text)}

        {/* 外部リンクプレビュー */}
        {external && (
          <TouchableOpacity
            style={styles.externalLinkContainer}
            onPress={() => Linking.openURL(external.uri)}
            activeOpacity={0.8}
          >
            {external.thumb && (
              <View style={styles.externalLinkImageContainer}>
                <Image
                  source={{ uri: external.thumb }}
                  style={styles.externalLinkImage}
                  contentFit="cover"
                />
                {external.uri.match(/youtube\.com|youtu\.be/) && (
                  <VideoDurationBadge 
                    initialDuration={duration || ''} 
                    videoUrl={external.uri} 
                    videoType="youtube" 
                  />
                )}
              </View>
            )}
            <View style={styles.externalLinkContent}>
              <Text style={styles.externalLinkTitle} numberOfLines={2}>
                {external.title}
              </Text>
              {external.description && (
                <Text style={styles.externalLinkDescription} numberOfLines={2}>
                  {external.description}
                </Text>
              )}
              <Text style={styles.externalLinkUrl} numberOfLines={1}>
                {external.uri}
              </Text>
            </View>
          </TouchableOpacity>
        )}

        {/* 動画 */}
        {isVideo && (() => {
          const videoData = convertPostToVideoPost(post);
          return (
            <View style={styles.videoContainer}>
              <View style={styles.videoPlaceholder}>
                <Ionicons name="play-circle" size={64} color={colors.primary} />
                <Text style={styles.videoText}>動画</Text>
                <VideoDurationBadge 
                  initialDuration={duration || ''} 
                  videoUrl={videoData?.videoUrl} 
                  videoType={videoData?.type} 
                />
              </View>
            </View>
          );
        })()}

        {/* 画像 */}
        {images.length > 0 && (
          <View style={styles.imagesContainer}>
            {images.slice(0, 4).map((image, index) => (
              <TouchableOpacity
                key={index}
                activeOpacity={0.9}
                onPress={() => {
                  setImageIndex(index);
                  setIsImageViewVisible(true);
                }}
              >
                <Image
                  source={{ uri: image.thumb }}
                  style={[
                    styles.image,
                    images.length === 1 ? styles.imageSingle : null,
                    images.length === 2 ? styles.imageDouble : null,
                    images.length > 2 ? styles.imageMultiple : null,
                  ] as any}
                  contentFit="cover"
                  transition={200}
                />
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* アクションボタン */}
        <View style={styles.actions}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={onReply}
            activeOpacity={0.7}
          >
            <Ionicons name="chatbubble-outline" size={18} color={colors.textSecondary} />
            {post.replyCount > 0 && (
              <Text style={styles.actionCount}>{post.replyCount}</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={onRepost}
            activeOpacity={0.7}
          >
            <Ionicons
              name="repeat-outline"
              size={20}
              color={isReposted ? colors.repost : colors.textSecondary}
            />
            {post.repostCount > 0 && (
              <Text style={[styles.actionCount, isReposted && styles.actionCountActive]}>
                {post.repostCount}
              </Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={onLike}
            activeOpacity={0.7}
          >
            <Ionicons
              name={isLiked ? 'heart' : 'heart-outline'}
              size={18}
              color={isLiked ? colors.like : colors.textSecondary}
            />
            {post.likeCount > 0 && (
              <Text style={[styles.actionCount, isLiked && styles.actionCountLike]}>
                {post.likeCount}
              </Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.actionButton} 
            onPress={onBookmark}
            activeOpacity={0.7}
          >
            <Ionicons 
              name={isBookmarked ? 'bookmark' : 'bookmark-outline'} 
              size={18} 
              color={isBookmarked ? colors.primary : colors.textSecondary} 
            />
          </TouchableOpacity>

          <TouchableOpacity style={styles.actionButton} activeOpacity={0.7}>
            <Ionicons name="share-outline" size={18} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
      </View>
      </TouchableOpacity>

      {/* 画像ビューア */}
      <ImageViewer
        images={imageUris}
        imageIndex={imageIndex}
        visible={isImageViewVisible}
        onRequestClose={() => setIsImageViewVisible(false)}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  wrapper: {
    backgroundColor: colors.background,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  repostReason: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    gap: spacing.xs,
  },
  repostReasonText: {
    ...typography.caption,
    color: colors.textSecondary,
    fontWeight: '600',
  },
  replyTo: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    gap: spacing.xs,
  },
  replyToText: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  container: {
    flexDirection: 'row',
    padding: spacing.md,
    backgroundColor: colors.background,
  },
  avatarContainer: {
    marginRight: spacing.sm,
  },
  content: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  displayName: {
    ...typography.body,
    fontWeight: '700',
    color: colors.text,
    marginRight: spacing.xs,
    maxWidth: '30%',
  },
  handle: {
    ...typography.bodySmall,
    color: colors.textSecondary,
    marginRight: spacing.xs,
    flex: 1,
  },
  separator: {
    ...typography.bodySmall,
    color: colors.textSecondary,
    marginRight: spacing.xs,
  },
  time: {
    ...typography.bodySmall,
    color: colors.textSecondary,
  },
  text: {
    ...typography.body,
    color: colors.text,
    lineHeight: 20,
    marginBottom: spacing.sm,
  },
  imagesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
    gap: spacing.xs,
  },
  image: {
    borderRadius: borderRadius.md,
    backgroundColor: colors.backgroundSecondary,
  },
  imageSingle: {
    width: '100%',
    height: 300,
  },
  imageDouble: {
    width: '49%',
    height: 200,
  },
  imageMultiple: {
    width: '49%',
    height: 150,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: spacing.sm,
    maxWidth: 400,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  actionCount: {
    ...typography.bodySmall,
    color: colors.textSecondary,
  },
  actionCountActive: {
    color: colors.repost,
  },
  actionCountLike: {
    color: colors.like,
  },
  hashtag: {
    color: colors.primary,
    fontWeight: '600',
  },
  mention: {
    color: colors.primary,
    fontWeight: '600',
  },
  link: {
    color: colors.link,
    textDecorationLine: 'underline',
  },
  externalLinkContainer: {
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.md,
    overflow: 'hidden',
  },
  externalLinkImageContainer: {
    position: 'relative',
    width: '100%',
  },
  externalLinkImage: {
    width: '100%',
    height: 180,
    backgroundColor: colors.backgroundSecondary,
  },
  externalLinkContent: {
    padding: spacing.sm,
  },
  externalLinkTitle: {
    ...typography.body,
    fontWeight: '600',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  externalLinkDescription: {
    ...typography.bodySmall,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  externalLinkUrl: {
    ...typography.caption,
    color: colors.textTertiary,
  },
  videoContainer: {
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
    borderRadius: borderRadius.md,
    overflow: 'hidden',
  },
  videoPlaceholder: {
    width: '100%',
    height: 200,
    backgroundColor: colors.backgroundSecondary,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  videoText: {
    ...typography.body,
    color: colors.textSecondary,
    marginTop: spacing.sm,
  },
});